import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { 
  Plane, Clock, Users, AlertTriangle, CheckCircle, 
  Loader2, Calendar, MapPin, Info,
  TrendingUp, ChevronDown, ChevronUp, Search, X,
  ThumbsUp, ThumbsDown, Minus, Star
} from 'lucide-react';
import AdBanner from '@/components/AdBanner';
import NewsletterSignup from '@/components/NewsletterSignup';
import ShareResults from '@/components/ShareResults';
import TimeSlider from '@/components/TimeSlider';
import FeedbackStats from '@/components/FeedbackStats';
import FavoriteStar from '@/components/FavoriteStar';
import PushReminder from '@/components/PushReminder';
import AppLogo from '@/components/AppLogo';
import LiveBusiestCTA from '@/components/LiveBusiestCTA';
import SEOHead, { buildOrganization } from '@/components/SEOHead';
import { getFavorites, subscribeFavorites, isFavorite } from '@/lib/favorites';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Searchable Airport Select Component
function AirportSelect({ airports, value, onChange, disabled, loading, favoriteCodes = [] }) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  
  // Get selected airport info
  const selectedAirport = airports.find(a => a.code === value);
  
  // Filter airports based on search
  const filteredAirports = useMemo(() => {
    if (!search.trim()) return airports;
    const searchLower = search.toLowerCase();
    return airports.filter(airport => 
      airport.code.toLowerCase().includes(searchLower) ||
      airport.city.toLowerCase().includes(searchLower) ||
      airport.name.toLowerCase().includes(searchLower) ||
      (airport.state && airport.state.toLowerCase().includes(searchLower))
    );
  }, [airports, search]);

  // Favorite airports (filtered to keep search consistent)
  const favoriteAirports = useMemo(() => {
    if (!favoriteCodes?.length) return [];
    const codeSet = new Set(favoriteCodes);
    return filteredAirports.filter((a) => codeSet.has(a.code));
  }, [filteredAirports, favoriteCodes]);

  // Group airports by country (excluding favorites to avoid duplicates)
  const groupedAirports = useMemo(() => {
    const favSet = new Set(favoriteCodes || []);
    const groups = { USA: [], International: [] };
    filteredAirports.forEach(airport => {
      if (favSet.has(airport.code)) return; // Skip favorites - shown above
      if (airport.country === 'USA') {
        groups.USA.push(airport);
      } else {
        groups.International.push(airport);
      }
    });
    return groups;
  }, [filteredAirports, favoriteCodes]);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Focus input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);
  
  const handleSelect = (airport) => {
    onChange(airport.code);
    setIsOpen(false);
    setSearch('');
  };
  
  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        data-testid="airport-select"
        className={`w-full h-11 px-3 text-left bg-slate-50 border border-transparent rounded-xl flex items-center justify-between
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-slate-100'}
          focus:outline-none focus:ring-2 focus:ring-[#FF5E00] focus:ring-offset-0`}
      >
        {loading ? (
          <span className="text-slate-400">Loading airports...</span>
        ) : selectedAirport ? (
          <span className="flex items-center gap-2">
            <span className="font-mono font-semibold text-[#FF5E00]">{selectedAirport.code}</span>
            <span className="text-slate-700">{selectedAirport.city}</span>
            {selectedAirport.state && (
              <span className="text-slate-400 text-sm">{selectedAirport.state}</span>
            )}
          </span>
        ) : (
          <span className="text-slate-400">Search or select airport...</span>
        )}
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {/* Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.15 }}
            className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-xl overflow-hidden"
          >
            {/* Search Input */}
            <div className="p-2 border-b border-slate-200">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  ref={inputRef}
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Type to search by city, code, or name..."
                  data-testid="airport-search-input"
                  className="w-full h-9 pl-9 pr-8 bg-slate-50 border border-slate-200 rounded text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-blue-500"
                />
                {search && (
                  <button
                    onClick={() => setSearch('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
            
            {/* Airport List */}
            <div className="max-h-[300px] overflow-y-auto">
              {filteredAirports.length === 0 ? (
                <div className="p-4 text-center text-slate-400 text-sm">
                  No airports found for "{search}"
                </div>
              ) : (
                <>
                  {/* Favorites */}
                  {favoriteAirports.length > 0 && (
                    <>
                      <div className="px-3 py-2 text-xs font-semibold text-amber-600 bg-amber-50 sticky top-0 flex items-center gap-1.5">
                        <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                        Favorites ({favoriteAirports.length})
                      </div>
                      {favoriteAirports.map(airport => (
                        <button
                          key={`fav-${airport.code}`}
                          onClick={() => handleSelect(airport)}
                          data-testid={`favorite-option-${airport.code}`}
                          className={`w-full px-3 py-2 text-left flex items-center gap-3 hover:bg-amber-50 transition-colors
                            ${value === airport.code ? 'bg-amber-50' : ''}`}
                        >
                          <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400 flex-shrink-0" />
                          <span className="font-mono font-semibold text-[#FF5E00] w-10">{airport.code}</span>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm text-slate-700 truncate">
                              {airport.city}{airport.state ? `, ${airport.state}` : `, ${airport.country}`}
                            </div>
                            <div className="text-xs text-slate-400 truncate">{airport.name}</div>
                          </div>
                        </button>
                      ))}
                    </>
                  )}

                  {/* US Airports */}
                  {groupedAirports.USA.length > 0 && (
                    <>
                      <div className="px-3 py-2 text-xs font-semibold text-slate-500 bg-slate-50 sticky top-0">
                        🇺🇸 United States ({groupedAirports.USA.length})
                      </div>
                      {groupedAirports.USA.map(airport => (
                        <button
                          key={airport.code}
                          onClick={() => handleSelect(airport)}
                          data-testid={`airport-option-${airport.code}`}
                          className={`w-full px-3 py-2 text-left flex items-center gap-3 hover:bg-slate-100 transition-colors
                            ${value === airport.code ? 'bg-blue-50' : ''}`}
                        >
                          <span className="font-mono font-semibold text-[#FF5E00] w-10">{airport.code}</span>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm text-slate-700 truncate">{airport.city}, {airport.state}</div>
                            <div className="text-xs text-slate-400 truncate">{airport.name}</div>
                          </div>
                          <span className={`text-xs px-1.5 py-0.5 rounded ${
                            airport.size === 'large' ? 'bg-blue-100 text-blue-600' :
                            airport.size === 'medium' ? 'bg-green-100 text-green-600' :
                            'bg-slate-100 text-slate-500'
                          }`}>
                            {airport.size}
                          </span>
                        </button>
                      ))}
                    </>
                  )}
                  
                  {/* International Airports */}
                  {groupedAirports.International.length > 0 && (
                    <>
                      <div className="px-3 py-2 text-xs font-semibold text-slate-500 bg-slate-50 sticky top-0">
                        🌍 International ({groupedAirports.International.length})
                      </div>
                      {groupedAirports.International.map(airport => (
                        <button
                          key={airport.code}
                          onClick={() => handleSelect(airport)}
                          data-testid={`airport-option-${airport.code}`}
                          className={`w-full px-3 py-2 text-left flex items-center gap-3 hover:bg-slate-100 transition-colors
                            ${value === airport.code ? 'bg-blue-50' : ''}`}
                        >
                          <span className="font-mono font-semibold text-[#FF5E00] w-10">{airport.code}</span>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm text-slate-700 truncate">{airport.city}, {airport.country}</div>
                            <div className="text-xs text-slate-400 truncate">{airport.name}</div>
                          </div>
                          <span className={`text-xs px-1.5 py-0.5 rounded ${
                            airport.size === 'large' ? 'bg-blue-100 text-blue-600' :
                            airport.size === 'medium' ? 'bg-green-100 text-green-600' :
                            'bg-slate-100 text-slate-500'
                          }`}>
                            {airport.size}
                          </span>
                        </button>
                      ))}
                    </>
                  )}
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Crowd level colors and icons
const CROWD_CONFIG = {
  low: { 
    color: '#0E9F6E',  // Status green — clear to proceed
    bgColor: 'rgba(16, 185, 129, 0.07)', 
    borderColor: 'rgba(16, 185, 129, 0.25)',
    icon: CheckCircle,
    label: 'LOW',
    description: 'Light traffic expected',
    minScore: 0,
    maxScore: 5,
    nextTier: 'Medium'
  },
  medium: { 
    color: '#D97706',  // Caution amber
    bgColor: 'rgba(245, 158, 11, 0.08)', 
    borderColor: 'rgba(245, 158, 11, 0.25)',
    icon: Users,
    label: 'MEDIUM',
    description: 'Normal traffic levels',
    minScore: 5,
    maxScore: 12,
    nextTier: 'High'
  },
  high: { 
    color: '#EA580C',  // Departure orange
    bgColor: 'rgba(249, 115, 22, 0.08)', 
    borderColor: 'rgba(249, 115, 22, 0.25)',
    icon: TrendingUp,
    label: 'HIGH',
    description: 'Expect longer waits',
    minScore: 12,
    maxScore: 20,
    nextTier: 'Extreme'
  },
  extreme: { 
    color: '#DC2626',  // Alert red
    bgColor: 'rgba(239, 68, 68, 0.08)', 
    borderColor: 'rgba(239, 68, 68, 0.25)',
    icon: AlertTriangle,
    label: 'EXTREME',
    description: 'Significant delays likely',
    minScore: 20,
    maxScore: 100,
    nextTier: null
  }
};

// Score tooltip component
const ScoreTooltip = ({ score, tier, config }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipRef = useRef(null);
  
  const pointsToNext = config.nextTier 
    ? Math.max(0, (config.maxScore - score)).toFixed(1)
    : null;
  
  const pointsIntoTier = (score - config.minScore).toFixed(1);
  
  return (
    <div className="relative inline-flex items-center">
      <button
        onClick={() => setShowTooltip(!showTooltip)}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        onFocus={() => setShowTooltip(true)}
        onBlur={() => setShowTooltip(false)}
        className="p-1.5 rounded-full hover:bg-slate-200 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400/50"
        aria-label="View score details"
        aria-describedby="score-tooltip"
      >
        <Info className="w-4 h-4 text-slate-400 hover:text-slate-600" />
      </button>
      
      {showTooltip && (
        <div
          ref={tooltipRef}
          id="score-tooltip"
          role="tooltip"
          className="absolute left-1/2 top-full transform -translate-x-1/2 mt-2 z-[100] w-72 p-4 rounded-xl bg-slate-800 border border-slate-700 shadow-2xl"
        >
          {/* Arrow pointing up */}
          <div className="absolute left-1/2 top-0 transform -translate-x-1/2 -translate-y-full">
            <div className="border-8 border-transparent border-b-slate-800"></div>
          </div>
          
          {/* Header */}
          <div className="flex items-center justify-between mb-3 pb-3 border-b border-slate-700">
            <span className="text-sm font-medium text-slate-300">Traffic Score</span>
            <span 
              className="text-lg font-bold"
              style={{ color: config.color }}
            >
              {score.toFixed(1)}
            </span>
          </div>
          
          {/* Current Tier */}
          <div className="mb-3">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs text-slate-500 uppercase tracking-wide">Current Tier</span>
            </div>
            <div className="flex items-center gap-2">
              <span 
                className="px-2 py-0.5 rounded text-sm font-medium"
                style={{ 
                  backgroundColor: `${config.color}20`,
                  color: config.color
                }}
              >
                {config.label}
              </span>
              <span className="text-xs text-slate-500">
                ({config.minScore} – {config.maxScore} range)
              </span>
            </div>
          </div>
          
          {/* Progress to next tier */}
          <div className="space-y-2">
            <div className="flex justify-between text-xs">
              <span className="text-slate-400">Tier Progress</span>
              <span className="text-slate-300">
                {((score - config.minScore) / (config.maxScore - config.minScore) * 100).toFixed(0)}%
              </span>
            </div>
            <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
              <div 
                className="h-full rounded-full transition-all duration-300"
                style={{ 
                  width: `${Math.min(100, ((score - config.minScore) / (config.maxScore - config.minScore)) * 100)}%`,
                  backgroundColor: config.color
                }}
              />
            </div>
            
            {/* Distance message */}
            <p className="text-xs text-slate-400 mt-2">
              {config.nextTier ? (
                <>
                  <span className="text-slate-300">{pointsToNext} points</span> until {config.nextTier} tier
                </>
              ) : (
                <>
                  <span className="text-slate-300">{pointsIntoTier} points</span> into {config.label} tier
                </>
              )}
            </p>
          </div>
          
          {/* Tier Reference */}
          <div className="mt-4 pt-3 border-t border-slate-700">
            <p className="text-xs text-slate-500 mb-2">All Tiers</p>
            <div className="space-y-1 text-xs">
              <div className="flex justify-between">
                <span className="text-emerald-400">Low</span>
                <span className="text-slate-500">0 – 5</span>
              </div>
              <div className="flex justify-between">
                <span className="text-amber-400">Medium</span>
                <span className="text-slate-500">5 – 12</span>
              </div>
              <div className="flex justify-between">
                <span className="text-orange-400">High</span>
                <span className="text-slate-500">12 – 20</span>
              </div>
              <div className="flex justify-between">
                <span className="text-red-400">Extreme</span>
                <span className="text-slate-500">20+</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default function AirportBusynessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Form state
  const [airports, setAirports] = useState([]);
  const [selectedAirport, setSelectedAirport] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [flightNumber, setFlightNumber] = useState('');
  const [isInternational, setIsInternational] = useState(false);
  const [hasTsaPrecheck, setHasTsaPrecheck] = useState(false);
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [loadingAirports, setLoadingAirports] = useState(true);
  const [prediction, setPrediction] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [submittingFeedback, setSubmittingFeedback] = useState(false);

  // Favorites state (driven from localStorage)
  const [favoriteCodes, setFavoriteCodes] = useState(() => getFavorites());
  
  // Subscribe to favorite changes
  useEffect(() => {
    const unsub = subscribeFavorites(setFavoriteCodes);
    return unsub;
  }, []);
  
  // Load airports on mount
  useEffect(() => {
    loadAirports();
    // Set default date/time to tomorrow at 10am
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setSelectedDate(tomorrow.toISOString().split('T')[0]);
    setSelectedTime('10:00');
    
    // Pre-fill airport from query param (e.g. /?airport=ATL from Live page deep-link)
    const airportParam = searchParams.get('airport');
    if (airportParam) {
      setSelectedAirport(airportParam.toUpperCase());
    }
  }, [searchParams]);
  
  // Reset feedback when prediction changes
  useEffect(() => {
    setFeedbackSubmitted(false);
  }, [prediction?.prediction_id]);
  
  const loadAirports = async () => {
    try {
      const res = await fetch(`${API}/airport/airports`);
      if (res.ok) {
        const data = await res.json();
        setAirports(data);
      }
    } catch (e) {
      console.error('Failed to load airports:', e);
      toast.error('Failed to load airports');
    } finally {
      setLoadingAirports(false);
    }
  };
  
  const submitFeedback = async (feedbackType) => {
    if (!prediction) return;
    
    setSubmittingFeedback(true);
    try {
      const res = await fetch(`${API}/airport/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prediction_id: prediction.prediction_id,
          airport_code: prediction.airport_code,
          predicted_level: prediction.crowd_level,
          actual_feedback: feedbackType,
          prediction_date: prediction.prediction_date,
          prediction_time: prediction.prediction_time
        })
      });
      
      if (res.ok) {
        setFeedbackSubmitted(true);
        toast.success('Thanks for your feedback!');
      } else {
        toast.error('Failed to submit feedback');
      }
    } catch (e) {
      console.error('Feedback error:', e);
      toast.error('Failed to submit feedback');
    } finally {
      setSubmittingFeedback(false);
    }
  };
  
  const handlePredict = async () => {
    if (!selectedAirport) {
      toast.error('Please select an airport');
      return;
    }
    if (!selectedDate || !selectedTime) {
      toast.error('Please select date and time');
      return;
    }
    
    setLoading(true);
    setPrediction(null);
    
    try {
      const res = await fetch(`${API}/airport/predict`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          airport_code: selectedAirport,
          date: selectedDate,
          time: selectedTime,
          flight_number: flightNumber || null,
          is_international: isInternational,
          has_tsa_precheck: hasTsaPrecheck
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setPrediction(data);
        toast.success('Prediction generated!');
      } else {
        const error = await res.json().catch(() => ({}));
        toast.error(error.detail || 'Failed to generate prediction');
      }
    } catch (e) {
      console.error('Prediction error:', e);
      toast.error('Failed to generate prediction');
    } finally {
      setLoading(false);
    }
  };
  
  const crowdConfig = prediction ? CROWD_CONFIG[prediction.crowd_level] : null;
  const CrowdIcon = crowdConfig?.icon || Users;
  
  // Format time for display - backend now sends 12-hour format
  const formatArriveBy = () => {
    if (!prediction) return '';
    return prediction.arrive_by;
  };
  
  const formatFlightTime = () => {
    if (!selectedTime) return '';
    const [hours, minutes] = selectedTime.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${minutes} ${ampm}`;
  };

  return (
    <div className="min-h-screen bg-[#F6F6F4] text-slate-900 relative overflow-hidden">
      <SEOHead
        title="How Busy Is My Airport — Real‑Time Crowd Levels & Best Arrival Times"
        description="Skip the airport guesswork. Get live crowd predictions for 331+ airports worldwide and know exactly when to leave for your flight. Free, no signup."
        path="/"
        keywords="airport crowd levels, when to arrive at airport, TSA wait times, airport traffic, flight delays, busy airports today, airport security wait, arrival time recommendations, FlightAware crowd"
        structuredData={[
          buildOrganization(),
          {
            '@context': 'https://schema.org',
            '@type': 'WebApplication',
            name: 'How Busy Is My Airport',
            url: 'https://howbusyismyairport.com',
            applicationCategory: 'TravelApplication',
            operatingSystem: 'Any',
            offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
            description: 'Real-time airport crowd predictions and arrival time recommendations for 331+ airports worldwide.',
            featureList: [
              'Live crowd predictions',
              'Smart arrival-time recommendations',
              'Push reminders for when to leave',
              'Favorite airports for one-tap access',
              'Day-overview crowd forecast',
              'Live "busiest airports right now" dashboard',
            ],
            aggregateRating: undefined,
          },
        ]}
      />

      {/* Technical grid backdrop + single departure-orange glow */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-tech-grid" />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full opacity-40"
        style={{
          background:
            'radial-gradient(closest-side, rgba(255,94,0,0.10), rgba(255,94,0,0) 70%)',
        }}
      />

      {/* Header — sticky glass */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-black/5">
        <div className="container mx-auto px-4 sm:px-6 py-3.5 sm:py-4">
          <div className="flex items-center justify-between gap-3">
            <Link to="/" className="flex items-center gap-2.5 sm:gap-3 group min-w-0" data-testid="brand-link">
              <AppLogo size={38} className="sm:[&>svg]:w-10 sm:[&>svg]:h-10 flex-shrink-0" />
              <div className="leading-tight min-w-0">
                <h1 className="heading text-[13px] sm:text-lg font-extrabold tracking-tight text-slate-900 leading-[1.1]">
                  How Busy Is My Airport
                </h1>
                <p className="hidden sm:block mono-label text-[9px] text-slate-400 mt-0.5">
                  Crowd intel · smarter departures
                </p>
              </div>
            </Link>
            <nav className="hidden lg:flex items-center gap-6 mr-2" data-testid="header-nav">
              <Link to="/airports" className="text-sm font-medium text-slate-700 hover:text-[#FF5E00] transition-colors">Airports</Link>
              <Link to="/insights" className="text-sm font-medium text-slate-700 hover:text-[#FF5E00] transition-colors">Insights</Link>
              <Link to="/blog" className="text-sm font-medium text-slate-700 hover:text-[#FF5E00] transition-colors">Blog</Link>
              <Link to="/faq" className="text-sm font-medium text-slate-700 hover:text-[#FF5E00] transition-colors">FAQ</Link>
              <Link to="/about" className="text-sm font-medium text-slate-700 hover:text-[#FF5E00] transition-colors">About</Link>
              <Link to="/contact" className="text-sm font-medium text-slate-700 hover:text-[#FF5E00] transition-colors" data-testid="nav-contact">Contact</Link>
            </nav>
            <LiveBusiestCTA />
          </div>
        </div>
      </header>

      {/* Editorial hero */}
      <section className="relative z-10 container mx-auto px-4 sm:px-6 max-w-5xl pt-8 sm:pt-12 mb-6 sm:mb-10">
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.05 }}
        >
          <div className="flex items-center gap-3 mb-4">
            <span className="mono-label text-[10px] sm:text-[11px] text-[#FF5E00]">
              Real-time crowd intelligence
            </span>
            <span className="hidden sm:block w-10 h-px bg-slate-300"></span>
            <span className="hidden sm:block mono-label text-[10px] text-slate-400">331 airports tracked</span>
          </div>
          <h2 className="heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-[-0.03em] text-slate-900 leading-[0.98] max-w-3xl">
            Beat the airport rush<span className="text-[#FF5E00]">.</span>
          </h2>
          <p className="mt-4 text-base sm:text-lg text-slate-500 max-w-xl leading-relaxed">
            Pick your airport and departure time — we forecast the security crush from live
            FlightAware data and tell you exactly when to head out.
          </p>
        </motion.div>
      </section>

      {/* Main Content */}
      <main className="relative z-10 container mx-auto px-4 sm:px-6 pb-16">
        {/* Favorites quick-pick */}
        {favoriteCodes.length > 0 && airports.length > 0 && (
          <div className="max-w-5xl mx-auto mb-4" data-testid="favorites-row">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="inline-flex items-center gap-1 text-xs text-slate-500 font-medium">
                <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                Favorites:
              </span>
              {favoriteCodes
                .map((c) => airports.find((a) => a.code === c))
                .filter(Boolean)
                .map((a) => (
                  <button
                    key={a.code}
                    onClick={() => setSelectedAirport(a.code)}
                    data-testid={`favorite-chip-${a.code}`}
                    className={`text-xs px-3 py-1 rounded-full border transition-colors ${
                      selectedAirport === a.code
                        ? 'bg-slate-900 border-slate-900 text-white'
                        : 'bg-white border-slate-200 text-slate-700 hover:border-[#FF5E00] hover:text-[#FF5E00]'
                    }`}
                  >
                    <span className="font-mono font-semibold">{a.code}</span>
                    <span className="text-slate-400 ml-1.5">{a.city}</span>
                  </button>
                ))}
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-2 gap-6 max-w-5xl mx-auto">
          
          {/* Input Card */}
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
          >
            <Card className="border-slate-200 bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
              <CardHeader>
                <CardTitle className="heading text-xl font-extrabold tracking-tight flex items-center gap-2 text-slate-900">
                  <Calendar className="w-5 h-5 text-[#FF5E00]" />
                  Flight Details
                </CardTitle>
                <CardDescription className="text-slate-500">
                  Enter your flight information to get a crowd prediction
                </CardDescription>
              </CardHeader>
              
              <CardContent className="space-y-5">
                {/* Airport Select */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="airport" className="mono-label text-[10px] text-slate-500">Airport</Label>
                    {selectedAirport && (
                      <FavoriteStar code={selectedAirport} size={16} />
                    )}
                  </div>
                  <AirportSelect
                    airports={airports}
                    value={selectedAirport}
                    onChange={setSelectedAirport}
                    disabled={loading}
                    loading={loadingAirports}
                    favoriteCodes={favoriteCodes}
                  />
                  <p className="text-xs text-slate-500">
                    {airports.length} airports available • Type to search by city, code, or name
                  </p>
                </div>

                {/* Date & Time */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="date" className="mono-label text-[10px] text-slate-500">Date</Label>
                    <Input
                      id="date"
                      type="date"
                      data-testid="date-input"
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                      disabled={loading}
                      className="bg-slate-50 border-transparent rounded-xl focus-visible:ring-[#FF5E00] h-11 text-slate-800"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="time" className="mono-label text-[10px] text-slate-500">Departure Time</Label>
                    <Input
                      id="time"
                      type="time"
                      data-testid="time-input"
                      value={selectedTime}
                      onChange={(e) => setSelectedTime(e.target.value)}
                      disabled={loading}
                      className="bg-slate-50 border-transparent rounded-xl focus-visible:ring-[#FF5E00] h-11 text-slate-800"
                    />
                  </div>
                </div>

                {/* Flight Number (Optional) */}
                <div className="space-y-2">
                  <Label htmlFor="flight" className="mono-label text-[10px] text-slate-500">
                    Flight Number <span className="text-slate-400 normal-case tracking-normal font-normal" style={{ fontFamily: 'var(--font-body)' }}>(optional)</span>
                  </Label>
                  <Input
                    id="flight"
                    type="text"
                    data-testid="flight-input"
                    value={flightNumber}
                    onChange={(e) => setFlightNumber(e.target.value.toUpperCase())}
                    disabled={loading}
                    placeholder="e.g. UA 1234"
                    className="bg-slate-50 border-transparent rounded-xl focus-visible:ring-[#FF5E00] h-11 uppercase text-slate-800"
                  />
                </div>

                {/* Travel Options */}
                <div className="space-y-3 pt-2">
                  <Label className="mono-label text-[10px] text-slate-500">Travel Options</Label>
                  
                  {/* International Flight Toggle */}
                  <label className="flex items-center gap-3 cursor-pointer group">
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={isInternational}
                        onChange={(e) => setIsInternational(e.target.checked)}
                        className="sr-only peer"
                        data-testid="international-toggle"
                      />
                      <div className="w-10 h-6 bg-slate-200 rounded-full peer-checked:bg-slate-900 transition-colors"></div>
                      <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full shadow peer-checked:translate-x-4 transition-transform"></div>
                    </div>
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-sm text-slate-600 group-hover:text-slate-800">International Flight</span>
                    </div>
                  </label>

                  {/* TSA PreCheck Toggle */}
                  <label className="flex items-center gap-3 cursor-pointer group">
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={hasTsaPrecheck}
                        onChange={(e) => setHasTsaPrecheck(e.target.checked)}
                        className="sr-only peer"
                        data-testid="precheck-toggle"
                      />
                      <div className="w-10 h-6 bg-slate-200 rounded-full peer-checked:bg-blue-500 transition-colors"></div>
                      <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full shadow peer-checked:translate-x-4 transition-transform"></div>
                    </div>
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                      <span className="text-sm text-slate-600 group-hover:text-slate-800">TSA PreCheck / CLEAR</span>
                    </div>
                  </label>
                </div>

                {/* Submit Button */}
                <Button
                  onClick={handlePredict}
                  data-testid="predict-button"
                  disabled={loading || !selectedAirport || !selectedDate || !selectedTime}
                  className="w-full h-14 rounded-full bg-[#FF5E00] hover:bg-[#E05300] text-white font-bold text-lg tracking-tight shadow-[0_8px_24px_rgba(255,94,0,0.3)] hover:shadow-[0_12px_28px_rgba(255,94,0,0.4)] hover:-translate-y-0.5 active:translate-y-0 transition-[transform,box-shadow,background-color] duration-200"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <TrendingUp className="w-4 h-4 mr-2" />
                      Check Busyness
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </motion.div>

          {/* Results Card */}
          <AnimatePresence mode="wait">
            {prediction ? (
              <motion.div
                key="result"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.2 }}
              >
                <Card 
                  className="border-slate-200 bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] relative overflow-hidden"
                  style={{ borderColor: crowdConfig?.borderColor }}
                >
                  {/* Share Button */}
                  <div className="absolute top-4 right-4 z-10">
                    <ShareResults
                      airport={airports.find(a => a.code === prediction.airport_code)}
                      date={selectedDate}
                      time={selectedTime}
                      crowdLevel={prediction.crowd_level}
                      arrivalTime={prediction.arrive_by}
                    />
                  </div>
                  
                  {/* Crowd Level Header - Light theme */}
                  <div 
                    className="p-6 text-center"
                    style={{ background: crowdConfig?.bgColor }}
                  >
                    <div className="flex justify-center mb-3">
                      <div 
                        className="w-16 h-16 rounded-full flex items-center justify-center bg-white shadow-sm"
                        style={{ boxShadow: `0 0 20px ${crowdConfig?.color}30` }}
                      >
                        <CrowdIcon 
                          className="w-8 h-8" 
                          style={{ color: crowdConfig?.color }}
                        />
                      </div>
                    </div>
                    <div className="flex items-center justify-center gap-2">
                      <h2 
                        className="heading text-2xl font-bold tracking-wide"
                        style={{ color: crowdConfig?.color }}
                        data-testid="crowd-level"
                      >
                        {crowdConfig?.label} TRAFFIC
                      </h2>
                      <ScoreTooltip 
                        score={prediction.crowd_score} 
                        tier={prediction.crowd_level}
                        config={crowdConfig}
                      />
                      <div className="relative group">
                        <button
                          type="button"
                          className="p-1.5 rounded-full hover:bg-white/60 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400/50"
                          aria-label="Traffic analysis based on flight density"
                          data-testid="traffic-analysis-info"
                        >
                          <Info className="w-4 h-4 text-slate-400 group-hover:text-slate-600" />
                        </button>
                        <div
                          role="tooltip"
                          className="pointer-events-none absolute left-1/2 top-full transform -translate-x-1/2 mt-2 z-[100] w-56 p-2 rounded-lg bg-slate-800 text-xs text-slate-200 shadow-xl opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity"
                        >
                          Traffic analysis based on flight density
                          <div className="absolute left-1/2 top-0 transform -translate-x-1/2 -translate-y-full">
                            <div className="border-8 border-transparent border-b-slate-800"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <p className="text-slate-500 mt-1">
                      {crowdConfig?.description}
                    </p>
                    {/* Terminal Badge */}
                    {prediction.is_terminal_specific && prediction.terminal && (
                      <div className="mt-3">
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/80 shadow-sm text-sm">
                          <MapPin className="w-3.5 h-3.5 text-blue-500" />
                          <span className="text-slate-700">Terminal {prediction.terminal}</span>
                          {prediction.gate && (
                            <span className="text-slate-400">• Gate {prediction.gate}</span>
                          )}
                        </span>
                      </div>
                    )}
                    
                    {/* Live FAA delay status (near-term predictions only) */}
                    {prediction.airport_delay && (
                      <div className="mt-3 flex justify-center">
                        <span
                          data-testid="prediction-delay-badge"
                          title={prediction.airport_delay.reason || undefined}
                          className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-full shadow-sm ${
                            prediction.airport_delay.color === 'green'
                              ? 'bg-white/80 text-emerald-700'
                              : prediction.airport_delay.color === 'red'
                                ? 'bg-rose-100 text-rose-700'
                                : 'bg-amber-100 text-amber-700'
                          }`}
                        >
                          {prediction.airport_delay.color === 'green' ? (
                            <>
                              <CheckCircle className="w-3.5 h-3.5" />
                              No FAA delays at {prediction.airport_code} right now
                            </>
                          ) : (
                            <>
                              <AlertTriangle className="w-3.5 h-3.5" />
                              FAA delays at {prediction.airport_code} — avg {Math.round(prediction.airport_delay.delay_secs / 60)} min
                            </>
                          )}
                        </span>
                      </div>
                    )}
                    
                    {/* User Feedback Stats */}
                    <FeedbackStats 
                      airportCode={prediction.airport_code}
                      className="mt-3"
                    />

                    {/* Departures / Arrivals compact stats */}
                    {(() => {
                      // Hide arrivals tile when we didn't fetch them (default now, to cut AeroAPI cost).
                      // Terminal-specific mode reuses the 2nd tile for "Total Airport" so we keep it in that case.
                      const showArrivals = prediction.is_terminal_specific || (prediction.arrivals_count || 0) > 0;
                      return (
                        <div className={`mt-4 grid ${showArrivals ? 'grid-cols-2' : 'grid-cols-1'} gap-3 max-w-xs mx-auto`}>
                          <div className="bg-white/70 backdrop-blur-sm rounded-xl p-3 border border-white/60 text-center">
                            {prediction.same_airline_departures !== null ? (
                              <>
                                <p className="text-xl font-bold text-slate-800 leading-tight" data-testid="departures-count">
                                  {prediction.departures_count} / {prediction.same_airline_departures}
                                </p>
                                <p className="text-[10px] text-slate-500 mt-0.5">
                                  Departures / {prediction.user_airline}
                                </p>
                              </>
                            ) : (
                              <>
                                <p className="text-xl font-bold text-slate-800 leading-tight" data-testid="departures-count">
                                  {prediction.departures_count}
                                </p>
                                <p className="text-[10px] text-slate-500 mt-0.5">
                                  {prediction.is_terminal_specific ? `Terminal ${prediction.terminal}` : 'Departures'}
                                </p>
                              </>
                            )}
                          </div>
                          {showArrivals && (
                            <div className="bg-white/70 backdrop-blur-sm rounded-xl p-3 border border-white/60 text-center">
                              <p className="text-xl font-bold text-slate-800 leading-tight" data-testid="arrivals-count">
                                {prediction.is_terminal_specific ? prediction.total_airport_departures : prediction.arrivals_count}
                              </p>
                              <p className="text-[10px] text-slate-500 mt-0.5">
                                {prediction.is_terminal_specific ? 'Total Airport' : 'Arrivals'}
                              </p>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>

                  <CardContent className="p-6 space-y-6">
                    {/* Arrival Times - Two Options */}
                    {/* Arrival Times - Two Options (boarding-pass tear-off) */}
                    <div className="border-b-2 border-dashed border-slate-200 pb-6">
                      <p className="mono-label text-[10px] text-slate-400 mb-4 text-center">Recommended arrival time</p>
                      
                      <div className="grid grid-cols-2 gap-4">
                        {/* Carry-on Only */}
                        <div className="bg-slate-50 rounded-xl p-4 border border-slate-100 text-center">
                          <div className="flex items-center justify-center gap-2 mb-2">
                            <svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span className="mono-label text-[9px] text-slate-500">Carry-on Only</span>
                          </div>
                          <p 
                            className="text-2xl font-bold heading"
                            style={{ color: crowdConfig?.color }}
                            data-testid="arrive-by-carryon"
                          >
                            {prediction.arrive_by}
                          </p>
                          <p className="text-xs text-slate-400 mt-1">
                            Skip the counter
                          </p>
                        </div>
                        
                        {/* Checking Bags */}
                        <div className="bg-slate-50 rounded-xl p-4 border border-slate-100 text-center">
                          <div className="flex items-center justify-center gap-2 mb-2">
                            <svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                            </svg>
                            <span className="mono-label text-[9px] text-slate-500">Checking Bags</span>
                          </div>
                          <p 
                            className="text-2xl font-bold heading"
                            style={{ color: crowdConfig?.color }}
                            data-testid="arrive-by-checked"
                          >
                            {prediction.arrive_by_checked}
                          </p>
                          <p className="text-xs text-slate-400 mt-1">
                            +{prediction.minutes_early_checked - prediction.minutes_early} min for counter
                          </p>
                        </div>
                      </div>
                      
                      {/* Timing context */}
                      <p className="text-slate-400 text-xs mt-3 text-center">
                        {prediction.minutes_early - prediction.standard_minutes > 0 
                          ? `${prediction.minutes_early - prediction.standard_minutes} min earlier than usual due to crowd levels`
                          : prediction.minutes_early - prediction.standard_minutes < 0
                            ? `${Math.abs(prediction.minutes_early - prediction.standard_minutes)} min later than usual - light crowds expected`
                            : 'Standard arrival time based on current conditions'
                        }
                      </p>

                      {/* Push notification reminder */}
                      <div className="mt-4">
                        <PushReminder
                          airportCode={prediction.airport_code}
                          arriveBy={prediction.arrive_by}
                          dateStr={selectedDate}
                        />
                      </div>
                    </div>

                    {/* Terminal-specific note */}
                    {prediction.is_terminal_specific && (
                      <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-center">
                        <p className="text-xs text-blue-600">
                          📍 Prediction specific to Terminal {prediction.terminal} security checkpoint
                        </p>
                      </div>
                    )}

                    {/* Reason */}
                    <div className="bg-slate-50 rounded-lg p-4 border border-slate-100">
                      <div className="flex items-start gap-3">
                        <Info className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-sm text-slate-600" data-testid="prediction-reason">
                            {prediction.reason}
                          </p>
                          <div className="flex items-center gap-3 mt-2 flex-wrap">
                            <p className="text-xs text-slate-500">
                              Confidence: <span className={`capitalize font-medium ${
                                prediction.confidence === 'high' ? 'text-green-600' : 'text-amber-600'
                              }`}>{prediction.confidence}</span>
                            </p>
                            {(() => {
                              const src = prediction.data_source || (prediction.confidence === 'high' ? 'live' : 'simulated');
                              if (src === 'live') return (
                                <span className="mono-label text-[9px] px-2 py-1 rounded bg-emerald-50 text-emerald-700 border border-emerald-100 flex items-center gap-1.5" data-testid="data-source-badge">
                                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                                  Live Data
                                </span>
                              );
                              if (src === 'schedules') return (
                                <span className="mono-label text-[9px] px-2 py-1 rounded bg-slate-100 text-slate-700 border border-slate-200 flex items-center gap-1.5" data-testid="data-source-badge">
                                  <Calendar className="w-3 h-3" />
                                  Airline Schedules
                                </span>
                              );
                              return (
                                <span className="mono-label text-[9px] px-2 py-1 rounded bg-amber-50 text-amber-700 border border-amber-100 flex items-center gap-1.5" data-testid="data-source-badge">
                                  <Clock className="w-3 h-3" />
                                  Estimated
                                </span>
                              );
                            })()}
                          </div>
                          {/* Context note per data source */}
                          {prediction.data_source === 'schedules' && (
                            <p className="text-xs text-slate-500 mt-2 bg-blue-50 px-3 py-2 rounded-lg border border-blue-100" data-testid="schedules-note">
                              <span className="text-slate-600">
                                Based on published airline schedules for your travel date. Live flight data (with delays and gate info) kicks in within 2 days of departure.
                              </span>
                            </p>
                          )}
                          {/* Show info message when using estimated data */}
                          {(prediction.data_source || 'simulated') === 'simulated' && prediction.confidence !== 'high' && (
                            <p className="text-xs text-slate-500 mt-2 bg-slate-100 px-3 py-2 rounded-lg border border-slate-200">
                              💡 <span className="text-slate-600">
                                {prediction.live_data_available_date 
                                  ? `Live flight data will be available on ${prediction.live_data_available_date}`
                                  : 'Live flight data is limited for this date/time'
                                }
                              </span>
                              <br />
                              <span className="text-slate-500">Your specific flight may not appear in the list. Enter your flight number for the most accurate prediction.</span>
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Live Flights Preview — first 3 always visible */}
                    {prediction.nearby_flights && prediction.nearby_flights.length > 0 && (
                      <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid="live-flights-card">
                        <div className="bg-slate-50 border-b border-slate-200 px-4 py-3 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {(prediction.data_source || 'simulated') === 'live' ? (
                              <>
                                <span className="relative flex h-2 w-2">
                                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                                </span>
                                <span className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Live Flight Data</span>
                              </>
                            ) : (
                              <>
                                <Plane className="w-3.5 h-3.5 text-slate-500" />
                                <span className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Nearby Flights</span>
                              </>
                            )}
                          </div>
                          <span className="text-[11px] text-slate-400 font-mono">
                            {prediction.nearby_flights.length} flights · ±2 hrs
                          </span>
                        </div>
                        <div className={`relative ${showDetails ? 'max-h-[360px] overflow-y-auto' : ''}`}>
                          {(showDetails ? prediction.nearby_flights : prediction.nearby_flights.slice(0, 3)).map((flight, idx) => (
                            <div
                              key={idx}
                              className="grid grid-cols-[52px_46px_1fr_70px] gap-3 px-4 py-2.5 items-center border-b border-slate-100 last:border-none text-sm"
                              data-testid="flight-row"
                            >
                              <span className="font-mono text-slate-700 text-xs">{flight.time}</span>
                              <span className={`text-[10px] px-1.5 py-0.5 rounded text-center font-medium ${
                                flight.direction === 'departure'
                                  ? 'bg-blue-50 text-blue-700 border border-blue-200'
                                  : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                              }`}>
                                {flight.direction === 'departure' ? 'DEP' : 'ARR'}
                              </span>
                              <div className="min-w-0">
                                <div className="flex items-baseline gap-2 flex-wrap">
                                  <span className="font-mono text-slate-800 text-xs font-medium">{flight.flight_number}</span>
                                  <span className="text-xs text-slate-500 truncate">{flight.airline}</span>
                                </div>
                                <p className="text-[11px] text-slate-400 truncate">
                                  {flight.direction === 'departure'
                                    ? `to ${flight.destination_city || '—'}`
                                    : `from ${flight.origin_city || '—'}`}
                                </p>
                              </div>
                              <span className="font-mono text-[11px] text-slate-400 text-right">{flight.aircraft_type}</span>
                            </div>
                          ))}
                          {!showDetails && prediction.nearby_flights.length > 3 && (
                            <div className="absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-t from-white to-transparent pointer-events-none"></div>
                          )}
                        </div>
                        {prediction.nearby_flights.length > 3 && (
                          <button
                            onClick={() => setShowDetails(!showDetails)}
                            className="w-full py-3 bg-slate-50 hover:bg-slate-100 text-slate-700 font-medium text-sm transition-colors border-t border-slate-200 flex justify-center items-center gap-2"
                            data-testid="expand-flights-btn"
                          >
                            {showDetails ? (
                              <>
                                <ChevronUp className="w-4 h-4" />
                                Collapse flights
                              </>
                            ) : (
                              <>
                                <ChevronDown className="w-4 h-4" />
                                View all {prediction.nearby_flights.length} nearby flights
                              </>
                            )}
                          </button>
                        )}
                        {showDetails && !flightNumber && (
                          <p className="text-xs text-slate-500 px-4 py-2.5 border-t border-slate-100 bg-slate-50/50">
                            💡 Don't see your flight? Enter your flight number above for the most accurate results.
                          </p>
                        )}
                      </div>
                    )}

                    {/* Feedback Section */}
                    <div className="border-t-2 border-dashed border-slate-200 pt-4 mt-2">
                      {feedbackSubmitted ? (
                        <div className="text-center py-3">
                          <CheckCircle className="w-5 h-5 text-green-500 mx-auto mb-2" />
                          <p className="text-sm text-slate-600">Thanks for your feedback!</p>
                          <p className="text-xs text-slate-400 mt-1">This helps us improve predictions</p>
                        </div>
                      ) : (
                        <div>
                          <p className="text-sm text-slate-500 text-center mb-3">
                            Were we accurate? Let us know when you're there!
                          </p>
                          <div className="flex gap-2 justify-center">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => submitFeedback('less_busy')}
                              disabled={submittingFeedback}
                              data-testid="feedback-less"
                              className="border-slate-300 text-slate-700 hover:bg-green-50 hover:border-green-500 hover:text-green-700"
                            >
                              <ThumbsDown className="w-4 h-4 mr-1.5" />
                              Less Busy
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => submitFeedback('as_expected')}
                              disabled={submittingFeedback}
                              data-testid="feedback-accurate"
                              className="border-slate-300 text-slate-700 hover:bg-blue-50 hover:border-blue-500 hover:text-blue-700"
                            >
                              <Minus className="w-4 h-4 mr-1.5" />
                              Accurate
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => submitFeedback('more_busy')}
                              disabled={submittingFeedback}
                              data-testid="feedback-more"
                              className="border-slate-300 text-slate-700 hover:bg-orange-50 hover:border-orange-500 hover:text-orange-700"
                            >
                              <ThumbsUp className="w-4 h-4 mr-1.5" />
                              More Busy
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ) : (
              <motion.div
                key="placeholder"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="hidden lg:flex"
              >
                <Card className="border-2 border-dashed border-slate-300 bg-white/60 rounded-2xl w-full flex items-center justify-center min-h-[400px]">
                  <div className="text-center p-8">
                    <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                      <Plane className="w-8 h-8 text-slate-400" />
                    </div>
                    <p className="mono-label text-[10px] text-slate-400 mb-2">Awaiting input</p>
                    <p className="text-slate-500">
                      Enter your flight details to see<br />crowd predictions
                    </p>
                  </div>
                </Card>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Time Slider - Day Overview (Full Width Below Grid) */}
        {selectedAirport && selectedDate && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
            className="max-w-5xl mx-auto mt-6"
          >
            <TimeSlider
              airport={selectedAirport}
              date={selectedDate}
              onTimeSelect={(time) => {
                setSelectedTime(time);
              }}
            />
          </motion.div>
        )}

        {/* Ad Banner - Middle (after results, before How It Works) */}
        {prediction && (
          <div className="max-w-5xl mx-auto mt-6">
            <AdBanner format="horizontal" className="rounded-lg overflow-hidden" />
          </div>
        )}

        {/* How It Works */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="max-w-5xl mx-auto mt-8"
        >
          <Card className="border-slate-200 bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
            <CardContent className="p-6 sm:p-8">
              <p className="mono-label text-[10px] text-[#FF5E00] mb-2">Methodology</p>
              <h3 className="heading text-2xl font-extrabold tracking-tight mb-6 text-slate-900">
                How It Works
              </h3>
              <div className="grid md:grid-cols-3 gap-4 text-sm">
                <div className="bg-slate-50 rounded-xl p-5 hover:bg-slate-100 transition-colors">
                  <div className="w-9 h-9 rounded-lg bg-slate-900 flex items-center justify-center mb-3">
                    <span className="text-white font-mono font-semibold text-sm">01</span>
                  </div>
                  <h4 className="heading font-bold text-slate-900 mb-1">Flight Analysis</h4>
                  <p className="text-slate-500 leading-relaxed">
                    We analyze all departures and arrivals scheduled within 2 hours of your flight time.
                  </p>
                </div>
                <div className="bg-slate-50 rounded-xl p-5 hover:bg-slate-100 transition-colors">
                  <div className="w-9 h-9 rounded-lg bg-slate-900 flex items-center justify-center mb-3">
                    <span className="text-white font-mono font-semibold text-sm">02</span>
                  </div>
                  <h4 className="heading font-bold text-slate-900 mb-1">Crowd Scoring</h4>
                  <p className="text-slate-500 leading-relaxed">
                    Each flight is weighted by aircraft size and direction to estimate passenger volume.
                  </p>
                </div>
                <div className="bg-slate-50 rounded-xl p-5 hover:bg-slate-100 transition-colors">
                  <div className="w-9 h-9 rounded-lg bg-[#FF5E00] flex items-center justify-center mb-3">
                    <span className="text-white font-mono font-semibold text-sm">03</span>
                  </div>
                  <h4 className="heading font-bold text-slate-900 mb-1">Smart Recommendation</h4>
                  <p className="text-slate-500 leading-relaxed">
                    Based on the crowd level, we suggest an optimal arrival time to avoid long waits.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* FAQ Section */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="max-w-5xl mx-auto mt-8"
        >
          <Card className="border-slate-200 bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
            <CardContent className="p-6 sm:p-8">
              <p className="mono-label text-[10px] text-[#FF5E00] mb-2">Answers</p>
              <h3 className="heading text-2xl font-extrabold tracking-tight mb-4 text-slate-900">
                Frequently Asked Questions
              </h3>
              <div className="space-y-4">
                <div className="border-b border-slate-100 pb-4">
                  <h4 className="font-medium text-slate-700 mb-2">How accurate are the predictions?</h4>
                  <p className="text-sm text-slate-500">
                    Our predictions are based on real-time flight data from FlightAware. For flights within 48 hours, 
                    we use live schedule data which is highly accurate. For dates further out, we provide estimates 
                    based on typical patterns.
                  </p>
                </div>
                <div className="border-b border-slate-100 pb-4">
                  <h4 className="font-medium text-slate-700 mb-2">How early should I arrive at the airport?</h4>
                  <p className="text-sm text-slate-500">
                    It depends on crowd levels. For domestic flights: 90 minutes (low) to 2.5 hours (extreme). 
                    For international: 2.5 hours (low) to 4 hours (extreme). Our tool gives you personalized recommendations.
                  </p>
                </div>
                <div className="border-b border-slate-100 pb-4">
                  <h4 className="font-medium text-slate-700 mb-2">Which airports are supported?</h4>
                  <p className="text-sm text-slate-500">
                    We support 331+ airports worldwide, including 128 US airports and 203 major international airports 
                    across all continents.
                  </p>
                </div>
                <div className="pb-2">
                  <h4 className="font-medium text-slate-700 mb-2">What factors affect airport busyness?</h4>
                  <p className="text-sm text-slate-500">
                    We consider the number of departures and arrivals, aircraft sizes (larger planes = more passengers), 
                    and the time window around your flight. Peak travel times like holidays and weekends typically show 
                    higher crowd levels.
                  </p>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-slate-100">
                <Link 
                  to="/blog" 
                  className="text-sm font-medium text-[#FF5E00] hover:text-[#E05300] transition-colors"
                >
                  Read more travel tips on our blog →
                </Link>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Ad Banner - Bottom */}
        <div className="max-w-5xl mx-auto mt-8 mb-4">
          <AdBanner format="horizontal" className="rounded-lg overflow-hidden" />
        </div>

        {/* Newsletter Signup */}
        <div className="max-w-5xl mx-auto mt-8 mb-8">
          <NewsletterSignup variant="card" />
        </div>

        {/* Footer */}
        <footer className="max-w-5xl mx-auto mt-16 mb-8">
          <div className="border-t border-slate-200 pt-10">
            {/* Navigation Links */}
            <div className="flex flex-wrap justify-center gap-x-5 gap-y-3 mb-8">
              <Link to="/about" className="text-sm text-slate-500 hover:text-[#FF5E00] transition-colors">About</Link>
              <Link to="/contact" className="text-sm text-slate-500 hover:text-[#FF5E00] transition-colors">Contact</Link>
              <Link to="/live" className="text-sm text-slate-500 hover:text-[#FF5E00] transition-colors">Live Busiest</Link>
              <Link to="/insights" className="text-sm text-slate-500 hover:text-[#FF5E00] transition-colors">Insights</Link>
              <Link to="/airports" className="text-sm text-slate-500 hover:text-[#FF5E00] transition-colors">Airport Guides</Link>
              <Link to="/blog" className="text-sm text-slate-500 hover:text-[#FF5E00] transition-colors">Blog</Link>
              <Link to="/faq" className="text-sm text-slate-500 hover:text-[#FF5E00] transition-colors">FAQ</Link>
              <Link to="/methodology" className="text-sm text-slate-500 hover:text-[#FF5E00] transition-colors">How It Works</Link>
              <Link to="/editorial-standards" className="text-sm text-slate-500 hover:text-[#FF5E00] transition-colors">Editorial Standards</Link>
              <Link to="/corrections" className="text-sm text-slate-500 hover:text-[#FF5E00] transition-colors">Corrections</Link>
              <Link to="/affiliate-disclosure" className="text-sm text-slate-500 hover:text-[#FF5E00] transition-colors">Affiliate Disclosure</Link>
              <Link to="/masthead" className="text-sm text-slate-500 hover:text-[#FF5E00] transition-colors">Masthead</Link>
              <Link to="/privacy" className="text-sm text-slate-500 hover:text-[#FF5E00] transition-colors">Privacy</Link>
              <Link to="/terms" className="text-sm text-slate-500 hover:text-[#FF5E00] transition-colors">Terms</Link>
            </div>

            {/* Sitewide affiliate disclosure — FTC 16 CFR Part 255 */}
            <p className="text-[11px] text-slate-500 text-center max-w-2xl mx-auto mb-6 leading-relaxed">
              <span className="font-semibold text-slate-700">Disclosure:</span> This
              site contains affiliate links (parking, credit cards, lounges). If
              you book or apply through them, we may earn a commission at no
              cost to you.{' '}
              <Link to="/affiliate-disclosure" className="text-[#FF5E00] hover:underline">
                Full disclosure →
              </Link>
            </p>
            
            {/* Contact / Credit */}
            <p className="text-slate-500 text-sm text-center">
              Built &amp; maintained by
            </p>
            <p className="text-slate-600 text-sm mt-2 text-center">
              <a
                href="https://clevelandcreativegroup.com/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-credit-link"
                className="text-[#FF5E00] hover:text-[#E05300] transition-colors font-medium"
              >
                Cleveland Creative Group
              </a>
            </p>
            
            {/* Copyright */}
            <p className="text-xs text-slate-400 text-center mt-4">
              © {new Date().getFullYear()} How Busy Is My Airport. All rights reserved.
            </p>

            {/* Massive typographic sign-off */}
            <p
              aria-hidden="true"
              className="heading select-none text-center font-extrabold tracking-[-0.04em] leading-none text-slate-900/[0.06] mt-10 text-[11vw] sm:text-[7vw] whitespace-nowrap overflow-hidden"
            >
              HOW BUSY IS MY AIRPORT
            </p>
          </div>
        </footer>
      </main>
    </div>
  );
}
